import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ProductsHero from './components/ProductsHero';
import ProductsGrid from './components/ProductsGrid';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';

function ProductsNavbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const goToSection = (hash: string) => {
    setMenuOpen(false);
    navigate(`/${hash}`);
  };

  const linkCls = (active = false) =>
    `text-sm tracking-wide transition-colors duration-200 cursor-pointer whitespace-nowrap font-medium ${
      active
        ? 'text-amber-500 border-b-2 border-amber-400 pb-0.5'
        : 'text-gray-600 hover:text-amber-600'
    }`;

  return (
    <nav className="fixed top-0 left-0 w-full z-50 bg-white/95 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center cursor-pointer">
          <img
            src="https://okayisu.com/common/img/okayisu-logo.png"
            alt="OKAYISU Logo"
            className="h-12 w-auto object-contain"
            onError={(e) => {
              const target = e.currentTarget;
              target.style.display = 'none';
              const fallback = target.nextElementSibling as HTMLElement;
              if (fallback) fallback.style.display = 'flex';
            }}
          />
          <span className="font-bold text-xl tracking-widest hidden" style={{ fontFamily: "'Montserrat', sans-serif", color: '#1a1a2e', display: 'none' }}>
            OKAYISU
          </span>
        </Link>

        <ul className="hidden md:flex items-center gap-8">
          <li><Link to="/" className={linkCls()} style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.home')}</Link></li>
          <li><button onClick={() => goToSection('#services')} className={`${linkCls()} bg-transparent border-none`} style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.services')}</button></li>
          <li><button onClick={() => goToSection('#about')} className={`${linkCls()} bg-transparent border-none`} style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.about')}</button></li>
          <li><Link to="/projeler" className={linkCls()} style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.projects')}</Link></li>
          <li><Link to="/urunler" className={linkCls(true)} style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.products')}</Link></li>
          <li><button onClick={() => goToSection('#contact')} className={`${linkCls()} bg-transparent border-none`} style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.contact')}</button></li>

          {/* Language Toggle */}
          <li className="flex items-center gap-0.5 border border-gray-200 rounded-full p-0.5">
            <button
              onClick={() => i18n.changeLanguage('tr')}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${i18n.language === 'tr' || i18n.language?.startsWith('tr') ? 'bg-amber-500 text-white' : 'text-gray-500 hover:text-amber-600'}`}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >TR</button>
            <button
              onClick={() => i18n.changeLanguage('en')}
              className={`px-2.5 py-1 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap ${i18n.language === 'en' || i18n.language?.startsWith('en') ? 'bg-amber-500 text-white' : 'text-gray-500 hover:text-amber-600'}`}
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >EN</button>
          </li>
        </ul>

        <button className="md:hidden w-8 h-8 flex items-center justify-center cursor-pointer text-gray-700" onClick={() => setMenuOpen(!menuOpen)}>
          <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-3-line'} text-2xl`} />
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white/98 backdrop-blur-md px-6 pb-6 border-t border-gray-100">
          <ul className="flex flex-col gap-1 pt-4">
            <li><Link to="/" onClick={() => setMenuOpen(false)} className="text-gray-700 hover:text-amber-600 text-base tracking-wide transition-colors duration-200 cursor-pointer block py-2 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.home')}</Link></li>
            <li><button onClick={() => goToSection('#services')} className="text-gray-700 hover:text-amber-600 text-base tracking-wide transition-colors duration-200 cursor-pointer block py-2 bg-transparent border-none w-full text-left font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.services')}</button></li>
            <li><button onClick={() => goToSection('#about')} className="text-gray-700 hover:text-amber-600 text-base tracking-wide transition-colors duration-200 cursor-pointer block py-2 bg-transparent border-none w-full text-left font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.about')}</button></li>
            <li><Link to="/projeler" onClick={() => setMenuOpen(false)} className="text-gray-700 hover:text-amber-600 text-base tracking-wide transition-colors duration-200 cursor-pointer block py-2 font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.projects')}</Link></li>
            <li><Link to="/urunler" onClick={() => setMenuOpen(false)} className="text-amber-500 text-base tracking-wide font-bold cursor-pointer block py-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.products')}</Link></li>
            <li><button onClick={() => goToSection('#contact')} className="text-gray-700 hover:text-amber-600 text-base tracking-wide transition-colors duration-200 cursor-pointer block py-2 bg-transparent border-none w-full text-left font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('navbar.contact')}</button></li>

            {/* Language Toggle (Mobile) */}
            <li className="pt-3 mt-3 border-t border-gray-100 flex items-center gap-2">
              <span className="text-gray-400 text-xs font-medium" style={{ fontFamily: "'Inter', sans-serif" }}>Language / Dil:</span>
              <button onClick={() => { i18n.changeLanguage('tr'); setMenuOpen(false); }} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer ${i18n.language === 'tr' || i18n.language?.startsWith('tr') ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-600'}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>TR</button>
              <button onClick={() => { i18n.changeLanguage('en'); setMenuOpen(false); }} className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all duration-200 cursor-pointer ${i18n.language === 'en' || i18n.language?.startsWith('en') ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-amber-50 hover:text-amber-600'}`} style={{ fontFamily: "'Montserrat', sans-serif" }}>EN</button>
            </li>
          </ul>
        </div>
      )}
    </nav>
  );
}

export default function ProductsPage() {
  const { t, i18n } = useTranslation();
  const isTR = i18n.language === 'tr' || i18n.language?.startsWith('tr');

  const seo = isTR
    ? {
        title: 'LED Aydınlatma Ürünleri | OKAYISU Profesyonel Armatürler',
        description: 'OKAYISU profesyonel LED aydınlatma ürün kataloğu. Dış cephe armatürleri, iç mekan LED, endüstriyel high-bay, mimari aydınlatma ve akıllı LED kontrol sistemleri. Üstün kalite ve enerji verimliliği.',
        keywords: 'LED aydınlatma ürünleri, profesyonel LED armatür, dış cephe aydınlatma, endüstriyel LED, mimari aydınlatma ürünleri',
        canonicalUrl: 'https://www.okayisu.com/urunler',
        lang: 'tr',
      }
    : {
        title: 'LED Lighting Products | OKAYISU Professional Luminaires',
        description: 'OKAYISU professional LED lighting product catalog. Facade luminaires, indoor LED, industrial high-bay, architectural lighting, and smart LED control systems. Superior quality and energy efficiency.',
        keywords: 'LED lighting products, professional LED luminaires, facade lighting, industrial LED, architectural lighting products',
        canonicalUrl: 'https://www.okayisu.com/en/urunler',
        lang: 'en',
      };

  return (
    <div className="bg-white min-h-screen">
      <SeoHead
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalUrl={seo.canonicalUrl}
        lang={seo.lang}
      />
      <ProductsNavbar />
      <div className="pt-20">
        <ProductsHero />
      </div>
      <ProductsGrid />

      <section className="bg-white py-20 px-6 border-t border-gray-100">
        <div className="max-w-3xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-5">
            <span className="w-1 h-1 bg-amber-500 rounded-full" />
            <span className="text-amber-600 text-xs tracking-[0.2em] uppercase font-semibold" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t('products.cta.badge')}</span>
          </div>
          <h2 className="text-gray-900 text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t('products.cta.heading1')}
            <span className="text-amber-500">{t('products.cta.heading2')}</span>
          </h2>
          <p className="text-gray-500 text-sm leading-relaxed mb-8 max-w-lg mx-auto" style={{ fontFamily: "'Inter', sans-serif" }}>{t('products.cta.desc')}</p>
          <Link to="/#contact" className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold px-8 py-4 rounded-full text-sm tracking-wide transition-all duration-300 cursor-pointer whitespace-nowrap" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            {t('products.cta.talkToExpert')}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}